/**
 * brand/Mark.tsx — the mark as it appears inside the page.
 *
 * The shapes are mapped straight out of `mark.ts`; there is no path data written by hand here, so this
 * component cannot drift from the favicon or from the committed assets — it has nothing of its own to drift
 * with.
 *
 * The gradient stops are coloured by CSS **class**, not by attribute. `stop-color="var(--logo-start)"` does
 * not work: a `var()` does not substitute inside an SVG presentation attribute. As a CSS declaration it
 * does, so `styles.css` owns the two liveries and the mark follows the theme with no JavaScript and no
 * prop — which also means it is correct for a viewer whose operating system is dark and who has never
 * touched the toggle, a case a `theme` prop would have to be told about.
 *
 * The mark is always `aria-hidden`. It is never the accessible name of anything: in the app bar the anchor
 * around it carries "Ticketless — automations", and on the sign-in card the heading beside it already says
 * the name. An `<svg role="img">` with its own label there would announce the application twice.
 */
import { useId } from "react";
import { MARK_BADGE_RADIUS, MARK_BARS, MARK_DOTS, MARK_GRID } from "./mark";

export interface MarkProps {
  /** Extra classes for sizing. The component sets no size of its own; the stylesheet decides. */
  readonly className?: string;
}

export function Mark({ className }: MarkProps): JSX.Element {
  // React 18 mints ids of the form `:r0:`, and a colon inside `url(#…)` is not something every renderer
  // agrees about. Stripping them keeps the reference plain while staying unique per instance.
  const gradientId = `mark-${useId().replace(/:/g, "")}`;

  return (
    <svg
      className={className === undefined ? "mark" : `mark ${className}`}
      viewBox={`0 0 ${MARK_GRID} ${MARK_GRID}`}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop className="mark__stop--start" offset="0" />
          <stop className="mark__stop--end" offset="1" />
        </linearGradient>
      </defs>
      <rect width={MARK_GRID} height={MARK_GRID} rx={MARK_BADGE_RADIUS} fill={`url(#${gradientId})`} />
      <g className="mark__ink">
        {MARK_DOTS.map((dot) => (
          <circle key={`dot-${dot.cy}`} cx={dot.cx} cy={dot.cy} r={dot.r} />
        ))}
        {MARK_BARS.map((bar) => (
          <rect
            key={`bar-${bar.y}`}
            x={bar.x}
            y={bar.y}
            width={bar.width}
            height={bar.height}
            rx={bar.radius}
          />
        ))}
      </g>
    </svg>
  );
}
